import { useEffect, useRef } from "preact/hooks";

/** Bars across the canvas. Enough to read as a voice, few enough to stay cheap. */
const BARS = 48;
const WIDTH = 480;
const HEIGHT = 96;

function token(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return value.trim() === "" ? fallback : value.trim();
}

function reducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * The live level of the microphone, drawn from an `AnalyserNode` on the real
 * stream. Every bar is a level this measured. Nothing here is invented: if the
 * audio context will not start, the area draws nothing and the recording
 * carries on regardless.
 */
export function Waveform({ stream }: { stream: MediaStream | null }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const element = canvas.current;
    if (!stream || !element || reducedMotion()) return;
    const Context = window.AudioContext;
    if (!Context) return;

    let context: AudioContext;
    let analyser: AnalyserNode;
    try {
      context = new Context();
      analyser = context.createAnalyser();
      analyser.fftSize = 512;
      context.createMediaStreamSource(stream).connect(analyser);
      // Created after an await, so the click that started recording no longer
      // counts as the gesture that unsuspends it.
      void context.resume();
    } catch {
      return;
    }

    const paint = element.getContext("2d");
    const samples = new Uint8Array(analyser.frequencyBinCount);
    // Each bar is one measured level, oldest on the left, so the column scrolls
    // as a person speaks instead of flickering on a single frame's samples.
    const levels = new Array<number>(BARS).fill(0);
    const bar = token("--brand", "#6D28D9");
    const ground = token("--surface-sunk", "#F7F5EE");
    let frame = 0;

    function draw(): void {
      frame = requestAnimationFrame(draw);
      if (!paint) return;
      analyser.getByteTimeDomainData(samples);
      let peak = 0;
      for (const sample of samples) peak = Math.max(peak, Math.abs(sample - 128));
      levels.shift();
      levels.push(peak / 128);

      paint.fillStyle = ground;
      paint.fillRect(0, 0, WIDTH, HEIGHT);
      paint.fillStyle = bar;
      const step = WIDTH / BARS;
      for (let index = 0; index < BARS; index++) {
        const height = Math.max(3, (levels[index] ?? 0) * HEIGHT * 0.9);
        paint.fillRect(index * step + 1, (HEIGHT - height) / 2, step - 2, height);
      }
    }
    draw();

    return () => {
      cancelAnimationFrame(frame);
      void context.close();
    };
  }, [stream]);

  // Decoration around a timer that already says the same thing in words, so
  // the whole area is hidden from a screen reader.
  if (reducedMotion()) return <div class="waveform-static" aria-hidden="true" />;
  return (
    <div aria-hidden="true">
      <canvas class="waveform" ref={canvas} width={WIDTH} height={HEIGHT} />
    </div>
  );
}
